import * as PIXI from 'pixi.js';
import * as React from 'react';
import moize from 'moize';
import BingMapsRoofsSelector from '__editor/bingMapsRoofsSelector/bingMapsRoofsSelector';
import FogOfWarComponent from '__common/components/FogOfWar';
import GoogleMapsRoofsSelector from '__editor/googleMapsRoofsSelector/googleMapsRoofsSelector';
import Loader from '__common/components/LoaderComponent';
import PanelsEditorControls from '__editor/panelsEditor/components/panelsEditorControls/panelsEditorControls';
import PanelsEditorDrawer from '__editor/panelsEditor/components/panelsEditorDrawer/panelsEditorDrawer';
import window from 'global/window';
import { clearTextures } from './models/utils/textureManager';
import { clearGeneratedPanelTextures } from './models/utils/panelTexture';
import { connect } from 'react-redux';
import { destroyRenderer, setupRenderer } from '__editor/panelsEditor/components/renderer/renderer';
import { determineZoomLevel } from '__editor/panelsEditor/components/background/utils/determineZoomLevel';
import { dispatch, state } from '__common/store';
import { drawCursorOnStage } from '__editor/panelsEditor/components/cursor/drawingCursorOnStage';
import { drawFogOfWarOnStage } from '__editor/panelsEditor/components/fogOfWar/drawFogOfWarOnStage';
import { drawingScaleToolOnStage } from '__editor/panelsEditor/components/scale/drawingScaleToolOnStage';
import { drawingMeasurementToolOnStage } from '__editor/panelsEditor/components/measurementTool/measurementToolOnStage';
import { drawRubberBandOnStage } from '__editor/panelsEditor/components/rubberBand/drawingRubberBandOnStage';
import { footerHeight, headerHeight } from '__common/constants/layout_css_dims';
import { getTiltedModuleRowSpacings, getTiltedModuleTableRowSpacingsGFT } from './components/tiltedModule/tiltedModule';
import { inchesToMeters } from 'maths';
import { initKeyboard } from '__editor/panelsEditor/keyboardConfg';
import { isRMFamily, products, isULA, isGFT, isAscender, isSMTiltPR, isEcoFoot2Plus, isRmGridflex10D, isRmGridflex5Rwdi } from '__common/constants/products';
import { loadRoofRowSpacing, loadRoofTableRowSpacing } from 'projectDesign/components/projectConfiguration/utils/loadRoofRowSpacing';
import { resetPanelEditorState } from './components/lifeCycle/panelsEditorUnmount';
import { resetRTree } from './components/panels/panelsCollisions';
import { setCanvas } from './components/canvas/canvas';
import { setEditorDims, getEditorDims, shouldUseVirtualRoofEdges } from './panelsEditorHelper';
import 'fpsmeter';
import {
  BG_CLEAR_LOADED,
  BG_LOADED,
  SET_BACKGROUND_XY_POS,
  SET_CANVAS_CENTER,
  SET_ROW_SPACING,
  RESET_HISTORY,
  CLEAR_HISTORY,
  SET_TABLE_ROW_SPACING,
  HIDE_CONFIRM_CLEAR_ARRAYS_MODAL,
} from 'actions';
import {
  drawCursor,
  removeCursor,
} from '__editor/panelsEditor/components/cursor/cursor';
import {
  drawLeadEdge,
  drawRotatedLeadEdge,
  removeLeadEdge,
  removeRotatedLeadEdge,
} from '__editor//panelsEditor/components/leadEdge/leadEdge';
import {
  getBingMapsImageLink,
  getGoogleMapImageLink,
} from '__editor/panelsEditor/models/backgrounds';
import {
  getScaleFromPitchRoof,
  loadRoofPitch,
  loadStructureTilt,
} from '__editor/panelsEditor/components/tiltedRoof/tiltedRoof';
import {
  initZoom,
  terminateZoom,
  zoomRoof,
  centerPanelsForBlankMap,
} from '__editor/panelsEditor/components/stage/zoom';
import {
  panelsEditorDidUpdate,
  shouldUpdateComponent,
} from './components/lifeCycle/panelsEditorDidUpdate';
import {
  getBackground,
  getBackgroundContainer,
  getCenterBgCords,
  removeBackground,
  createRoofEdges,
  createRoofDimensions,
} from '__editor/panelsEditor/components/background/background';
import {
  setPanelsDimensions,
} from '__editor/panelsEditor/components/panels/panels';
import {
  getMeterPerPixel,
  setStagedMouseEvents,
  setupStage,
  destroyPanelsEditorStage,
  updateLayersOrder,
} from '__editor/panelsEditor/components/stage/stage';
import { createObstructionContainer, loadObstructions } from './components/obstructions/obstructions';
import { drawObstructionsOnStage } from './components/obstructions/drawObstructionsOnStage';
import { drawRoofZonesOnStage } from './components/roofZones/drawRoofZonesOnStage';
import { removePanelsContainer, createPanelsContainer, createBaysContainer, removeBaysContainer } from './components/panels/utils/panelsContainer';
import { loadPanels } from './components/panels/utils/panelsManagment';
import { clearLastSnapGroup } from './components/panels/utils/panelsSnappingGroup';
import { PanelsContainer } from '__editor/panelsEditor/models/panelsContainer';
import { isBlankMap } from '__common/constants/map';
import { loadPanelsRotationDegrees } from './components/background/utils/rotatedPanels';
import { applyPanelRotationFactorChanges } from '__common/utils/versionCompare/versionCompare';
import { loadLowEdgeToRoof } from './components/background/utils/loadLowEdgeToRoof';
import { calculateSkewZoneCoords } from './components/skewZones/drawSkewZonesOnStage';
import { canShowSkewZone } from '__common/components/exposure/exposureHelper';
import arrayNumberingModel from './models/arrayNumbering';

/**
 * Memoized function to convert panels array to a dictionary for faster lookups.
 * Using moize with isSerialized: true to ensure stable results based on panels content.
 * This optimization avoids re-calculating the dictionary on every render if panels haven't changed.
 */
const getPanelsDict = moize((panels) => {
  return panels.reduce((acc, item) => {
    acc[item.id] = item;
    return acc;
  }, { isSerialized: true });
});

type state = {
  isImgLoading: boolean;
  zoomedIn: boolean;
  bgAdded: boolean;
};

/**
 * PanelsEditor Component
 * Responsible for managing the PIXI.js canvas and rendering the panel layout editor.
 */
class PanelsEditor extends React.Component<panelsEditorComponentState, state> {

  state = {
    isImgLoading: true,
    zoomedIn: false,
    bgAdded: false,
  };

  isUnmounted = false;

  canvasHeight = document.body.offsetHeight - headerHeight - footerHeight;

  screenshotsRes = {
    width: 1366,
    height: 768,
  };

  roofEdges: PIXI.Graphics;
  skewZones: PIXI.Graphics;
  roofDimensions: PIXI.Graphics;
  outerDiv: HTMLElement;
  renderer: any;
  resizeObserver: ResizeObserver | null = null;
  pendingResizeFrame: number | null = null;
  canvas: HTMLElement;
  animateFrame: number | null = null;
  panelsContainer: PanelsContainer;
  stopAnimation: boolean;
  stage: PIXI.Container;
  backgroundContainer: PIXI.Container;
  cursor: PIXI.Graphics;
  background: PIXI.Container;
  fogOfWar: any;
  baysContainer: PanelsContainer;
  arrayNumber: PIXI.Graphics;

  /**
   * Optimized shouldComponentUpdate using the helper function.
   * Compares props and state to prevent unnecessary re-renders.
   */
  shouldComponentUpdate(nextProps, nextState) {
    return shouldUpdateComponent(this.props, this.state, nextState, nextProps);
  }

  componentDidMount() {
    window.addEventListener('REFRESH_MEASUREMENT_TOOL', this.renderMeasurementToolOnEvent);
    const {
      roofsSelector: { mapType },
    } = this.props;

    this.loadMapImage(mapType);
  }

  /**
   * Handles updates to the component.
   * Calculates panelsDict and calls the centralized update helper.
   */
  componentDidUpdate(prevProps, prevState) {
    const { roofsSelector: { mapType } } = this.props;

    const panelsDict = getPanelsDict(this.props.panels.panels);
    panelsEditorDidUpdate(
      prevProps,
      this.props,
      prevState,
      this.state,
      this.outerDiv,
      this.runEditor,
      this.resetZoom,
      this.redrawBackground,
      this.redrawCursor,
      this.drawRubberBand,
      this.redrawLeadEdge,
      this.resizeBackgroundContainer,
      drawingScaleToolOnStage,
      this.redrawRotatedLeadEdge,
      this.drawRoofDimensions,
      this.drawArrayNumber,
      panelsDict,
    );

    if (
      isBlankMap(mapType) &&
      this.props.isUbuilderLite &&
      this.state.bgAdded &&
      this.props.panels.panels.length > prevProps.panels.panels.length
    ) {
      centerPanelsForBlankMap();
    }
  }

  renderMeasurementToolOnEvent = () => {
    drawingMeasurementToolOnStage();
  }

  /**
   * Cleanup PIXI resources and listeners when the component is unmounted.
   */
  componentWillUnmount() {
    this.isUnmounted = true;
    window.removeEventListener('REFRESH_MEASUREMENT_TOOL', this.renderMeasurementToolOnEvent);
    const { projectConfiguration: { projectEnvConfig: { is_aurora_project, is_os_project } } } = state();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.pendingResizeFrame !== null) {
      cancelAnimationFrame(this.pendingResizeFrame);
      this.pendingResizeFrame = null;
    }
    terminateZoom(this.canvas, this.stage, this.renderer);
    if (this.animateFrame !== null) {
      cancelAnimationFrame(this.animateFrame);
      this.animateFrame = null;
    }
    resetPanelEditorState();
    resetRTree();
    clearTextures();
    clearGeneratedPanelTextures();
    clearLastSnapGroup();
    if (!(is_aurora_project || is_os_project)) dispatch(RESET_HISTORY());
    dispatch(CLEAR_HISTORY());
    destroyPanelsEditorStage();
    destroyRenderer();
    this.onBgReload();
    this.stopAnimation = true;
    this.canvas = null;
    this.fogOfWar = undefined;
    document.body.style.cursor = 'default';
    removePanelsContainer();
    removeBaysContainer();
    if (this.props.isDialogOpen) dispatch(HIDE_CONFIRM_CLEAR_ARRAYS_MODAL());
  }

  getMapLink = (mapType: string) => {
    if (mapType === 'google') {
      return getGoogleMapImageLink();
    }

    if (mapType === 'bing') {
      return getBingMapsImageLink();
    }
  }

  loadMapImage = (mapType: string) => {
    const mapLink = this.getMapLink(mapType);

    if (PIXI.loader.resources[mapLink] || mapType === 'white') {
      this.finishImageLoading();
    } else {
      this.addMapImage(mapLink);
    }
  }

  addMapImage = (mapLink: string) => {
    PIXI.loader
      .add(mapLink)
      .on('complete', () =>
        this.finishImageLoading(),
      )
      .load();
  }

  finishImageLoading = () => {
    if (this.isUnmounted) return;
    this.setState(prevState => ({ isImgLoading: false }));
  }

  runEditor = () => {
    const { screenshots, projectConfiguration: { projectEnvConfig: { tilt }, productId } } = this.props;

    if (screenshots) {
      this.renderer = setupRenderer(this.screenshotsRes.width, this.screenshotsRes.height);
    } else {
      this.renderer = setupRenderer(this.outerDiv.offsetWidth, this.canvasHeight);
    }

    this.canvas.appendChild(this.renderer.view);
    const { dispatch } = this.props;

    const isGridflex = productId === products.rm_gridflex;
    const isRM5 = productId === products.rm_5;

    if (isRM5 || isGridflex || isSMTiltPR(productId) || isRmGridflex10D(productId) || isRmGridflex5Rwdi(productId) ) {
      loadRoofRowSpacing();
    } else if (tilt && !isULA(productId) && !isAscender(productId)) {
      const rowSpacing = getTiltedModuleRowSpacings(tilt);
      dispatch(SET_ROW_SPACING(inchesToMeters(rowSpacing)));
      if (isGFT(productId)) {
        const tableRowSpacing = getTiltedModuleTableRowSpacingsGFT(tilt);
        dispatch(SET_TABLE_ROW_SPACING(tableRowSpacing));
      }
    }

    this.initCanvas(productId);
    this.panelsContainer = createPanelsContainer();
    this.baysContainer = createBaysContainer();
    getMeterPerPixel();
    setPanelsDimensions();
    loadObstructions();
    loadPanels();

    // loadPanels() calls rotateBgToLeadEdge() which dispatches
    // BACKGROUND_SET_ROTATION synchronously. The reactive
    // shouldRedrawBackground check later in panelsEditorDidUpdate may
    // not always pick up the change (timing depends on React batching),
    // so redraw the background explicitly when rotation was applied.
    const { background: { rotationDegrees: appliedRotation } } = state();
    if (appliedRotation !== 0) {
      this.backgroundContainer.children = removeBackground(
        this.backgroundContainer.children,
      );
      this.drawBackground('google');
      this.drawRoofEdges();
      this.drawRoofDimensions();
      this.drawLeadEdge();
      this.drawRotatedLeadEdge();
      this.drawArrayNumber();
      this.stage.removeChild(this.fogOfWar);
      this.fogOfWar = drawFogOfWarOnStage(this.outerDiv.offsetWidth, this.canvasHeight, screenshots);
    }

    createObstructionContainer();
    drawObstructionsOnStage();
    initKeyboard(isRMFamily(productId));

    dispatch(
      SET_CANVAS_CENTER(this.outerDiv.offsetWidth / 2, this.canvasHeight / 2),
    );

    this.attachResizeObserver();
  }

  // The Pixi renderer captures outerDiv.offsetWidth once at mount (see
  // setupRenderer call above) and exposes no native reaction to container
  // resize — the canvas drawing buffer stays at its initial pixel width
  // even if the host container (the CSS Grid track in V3) widens. Without
  // this observer, collapsing a V3 side panel leaves an unrendered band
  // between the new container edge and the stale canvas.
  //
  // On each rAF-debounced resize tick we do three things:
  //   1. renderer.resize(w, h)        — resize the Pixi drawing buffer to
  //                                     the new container width
  //   2. resizeBackgroundContainer()  — re-scale the background sprite
  //                                     and redraw the fog of war at the
  //                                     new width
  //   3. setEditorDims(w, h)          — keep the helper-cached editor
  //                                     dims in sync with the new size
  //
  // We deliberately do NOT re-dispatch SET_CANVAS_CENTER on resize.
  // canvasCenter is the origin every piece of stage geometry (roof
  // polygons, panels, obstructions) was drawn against at mount. Readers
  // (cursor preview, rubberBand, fogOfWar, shadowPanel, obstruction)
  // use it as a passive drawing offset on demand; nothing watches for
  // changes to redraw. Changing it mid-session re-anchors only the
  // next-rendered shapes, producing a duplicate-roof / offset-cursor
  // visual against the already-placed geometry.
  //
  // Today this only matters in V3 (DesignWorkspace's grid-template-columns
  // animation is the only resize source). Legacy ProjectDesign and
  // ScreenshotContainer mount the editor in static containers; for them
  // the observer is a no-op (or skipped, in the screenshot case). The
  // logic lives here because PanelsEditor owns the renderer and the
  // dim-dependent state, regardless of which container hosts it.
  attachResizeObserver = () => {
    // Limit this behavior to the UB Lite flow only — legacy / classic
    // editor mounts in static containers and must not be affected.
    if (!this.props.isUbuilderLite) return;
    // Screenshot mode renders at fixed 1366x768 dimensions and swaps the
    // outerDiv element between loading and ready states, which would
    // leave the observer attached to a detached node. Skip entirely.
    if (this.props.screenshots) return;
    // jsdom in the unit test environment doesn't define ResizeObserver
    // and the shim file is out of scope for this change. Guard so the
    // editor still mounts in tests; the observer just isn't wired up.
    if (typeof ResizeObserver === 'undefined') return;
    // runEditor can fire more than once over the editor's lifetime
    // (view-mode flips re-enter through panelsEditorDidUpdate). Attach
    // only the first time.
    if (this.resizeObserver || !this.outerDiv) return;

    this.resizeObserver = new ResizeObserver(this.onContainerResize);
    this.resizeObserver.observe(this.outerDiv);
  }

  onContainerResize = () => {
    // Coalesce ResizeObserver bursts during a CSS transition into a
    // single per-frame resize call.
    if (this.pendingResizeFrame !== null) return;
    this.pendingResizeFrame = requestAnimationFrame(() => {
      this.pendingResizeFrame = null;
      this.handleContainerResize();
    });
  }

  handleContainerResize = () => {
    if (!this.outerDiv || !this.renderer) return;
    const newWidth = this.outerDiv.offsetWidth;
    // Pixi v4's .resize throws on a zero-size buffer; also guard the
    // collapsed-to-zero case for any future caller.
    if (newWidth <= 0) return;
    // Skip no-op ticks by comparing against the renderer's actual buffer
    // size — only this method writes to that. The earlier getEditorDims()
    // cache check was wrong because animate() updates setEditorDims on
    // every frame regardless of whether we've resized the renderer, so
    // the cache reflected the new container width before our rAF fired
    // and the guard short-circuited the resize that was supposed to act
    // on it.
    if (this.renderer.width === newWidth && this.renderer.height === this.canvasHeight) return;

    this.renderer.resize(newWidth, this.canvasHeight);
    this.resizeBackgroundContainer();
    setEditorDims(newWidth, this.canvasHeight);
    // renderer.resize() reassigns view.width/height on the underlying <canvas>,
    // which the browser clears synchronously. Without an explicit render here,
    // the canvas is blank until animate()'s next rAF tick — visible as a
    // flash during the 200ms grid-template-columns transition when V3 Lite
    // panels collapse. Redraw immediately so the wipe never reaches paint.
    if (this.props.isUbuilderLite && this.stage) this.renderer.render(this.stage);
  }

  initCanvas(productId: number) {
    const { screenshots, projectConfiguration: { projectVersion } } = this.props;
    this.stage = setupStage(this.outerDiv.offsetWidth, this.canvasHeight);
    window.stage = this.stage;
    determineZoomLevel(this.outerDiv.offsetWidth, this.canvasHeight);
    this.backgroundContainer = getBackgroundContainer();
    this.stage.addChild(this.backgroundContainer);
    this.drawBackground('google');
    setStagedMouseEvents(this.canvas);
    initZoom(this.canvas, this.stage, this.renderer);
    this.drawRoofEdges();
    this.drawRoofDimensions();
    this.drawArrayNumber();

    loadRoofPitch();
    if (isSMTiltPR(productId)) loadStructureTilt();

    if (applyPanelRotationFactorChanges(projectVersion)) loadPanelsRotationDegrees();

    if (isAscender(productId)) loadLowEdgeToRoof();

    this.animate();

    this.fogOfWar = drawFogOfWarOnStage(this.outerDiv.offsetWidth, this.canvasHeight, screenshots);

    this.cursor = drawCursor({ x: 0, y: 0 }, productId);
    this.stage.addChild(this.cursor);
  }

  drawfogOfWar() {

  }

  shouldWaitForInitialZoom = () => {
    const { screenshots } = this.props;
    return screenshots || this._shouldZoomResidentalProducts();
  }

  isEditorReady = () => {
    const {
      background: { bgLoaded },
    } = this.props;
    const { isImgLoading, bgAdded, zoomedIn } = this.state;

    if (isImgLoading || !bgLoaded || !bgAdded) {
      return false;
    }

    return !this.shouldWaitForInitialZoom() || zoomedIn;
  }

  getLoadingMessage = () => {
    const {
      background: { bgLoaded, selectedRoofId },
      drawingManager: { roofs },
    } = this.props;
    const { isImgLoading, bgAdded, zoomedIn } = this.state;
    const panelCount = roofs?.[selectedRoofId]?.panels?.length || 0;

    if (isImgLoading) {
      return 'Loading roof image...';
    }

    if (!bgAdded || !bgLoaded) {
      return panelCount ? `Opening roof and rendering ${panelCount} panels...` : 'Preparing panel editor...';
    }

    if (this.shouldWaitForInitialZoom() && !zoomedIn) {
      return 'Framing roof layout...';
    }

    return 'Opening panel editor...';
  }

  renderLoadingOverlay = (style = {}) => (
    <div
      style={{
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        zIndex: 20,
        background: 'rgba(255, 255, 255, 0.92)',
        ...style,
      }}
    >
      <Loader overrideMsg={this.getLoadingMessage()} fitParent={true} />
    </div>
  )

  resetZoom = () => {
    if (this.isUnmounted) return;
    this.setState(prevState => ({ zoomedIn: false }));
    this.stage.x = 0;
    this.stage.y = 0;
    this.stage.scale.set(1);
  }

  onBgReload = () => {
    const { dispatch } = this.props;
    if (!this.isUnmounted) this.setState({ bgAdded: false });
    dispatch(BG_CLEAR_LOADED());
  }

  redrawCursor = () => {
    const {
      productId,
      rubberBand: { mode },
      scale: { enabled },
      panels: { panelsToBeMoved },
      background: { moveArrayMode }
    } = this.props;

    if (!this.stage) {
      return;
    }

    this.stage.children = removeCursor(this.stage.children);

    if (mode === 'draw' || enabled || (moveArrayMode && panelsToBeMoved.length > 0)) {
      this.cursor = drawCursor({ x: 0, y: 0 }, products[productId]);
      this.stage.addChild(this.cursor);
    }
    drawObstructionsOnStage();
  }

  drawBackground(type = 'google') {
    const {
      dispatch,
      roofsSelector: { mapType },
    } = this.props;
    const { x, y, rotationDegrees } = getCenterBgCords(
      this.outerDiv.offsetWidth,
      this.canvasHeight,
    );
    this.background = getBackground(x, y, rotationDegrees, mapType);
    this.backgroundContainer.addChild(this.background);
    const backgroundScale: pixelPoint = {
      x: this.background.scale.x,
      y: this.background.scale.y,
    };
    dispatch(SET_BACKGROUND_XY_POS(x, y, backgroundScale));
    dispatch(CLEAR_HISTORY());
  }

  redrawBackground = () => {
    const { projectConfiguration: { productId, projectVersion }, roofsSelector: { mapType }, screenshots } = this.props;
    this.onBgReload();
    getMeterPerPixel();
    this.backgroundContainer.children = removeBackground(
      this.backgroundContainer.children,
    );
    this.drawBackground(this.props.background.backgroundType);
    this.drawRoofEdges();
    this.drawRoofDimensions();
    this.drawLeadEdge();
    this.drawRotatedLeadEdge();
    this.drawArrayNumber();

    this.stage.removeChild(this.fogOfWar);

    this.fogOfWar = undefined;

    drawFogOfWarOnStage(this.outerDiv.offsetWidth, this.canvasHeight, screenshots);

    const isGridflex = productId === products.rm_gridflex;
    const isRM5 = productId === products.rm_5;

    if (isRM5 || isGridflex || isAscender(productId) || isRmGridflex10D(productId)  || isRmGridflex5Rwdi(productId)) {
      loadRoofRowSpacing();
    }

    if (isGFT(productId) || isAscender(productId)) {
      loadRoofTableRowSpacing();
    }

    this.resizeBackgroundContainer();
    dispatch(CLEAR_HISTORY());
    clearLastSnapGroup();
    drawRoofZonesOnStage();
    canShowSkewZone(productId, mapType, projectVersion) && calculateSkewZoneCoords();
  }

  drawRubberBand = (prevState) => {
    if (!this.stage || !this.panelsContainer) {
      return;
    }

    const prevRubberBandState = prevState.rubberBand;
    drawRubberBandOnStage(
      this.stage,
      this.panelsContainer,
      prevRubberBandState,
    );
  }

  drawRoofEdges() {
    const {
      roofsSelector: { mapType },
      projectConfiguration: { productId, projectVersion },
    } = this.props;

    this.roofEdges = createRoofEdges();
    if (isBlankMap(mapType) && !shouldUseVirtualRoofEdges(mapType, productId, projectVersion)) {
      this.roofEdges.visible = false;
    }

    this.background.addChild(this.roofEdges);
  }

  drawRoofDimensions = () => {
    const {
      roofsSelector: { mapType },
      background: { showRoofDimensions }
    } = this.props;
    if (isBlankMap(mapType) || !this.stage) {
      return;
    }

    this.removeRoofDimensions();

    if (showRoofDimensions) {
      this.roofDimensions = createRoofDimensions(this.stage.scale.x);
      this.stage.addChild(this.roofDimensions);
    }

    return;
  }

  drawArrayNumber = () => {
    const { background: { showArrayNumber }, isUbuilderLite } = this.props;
    if (!this.stage) {
      return;
    }
    this.removeArrayNumber();
    if (showArrayNumber || isUbuilderLite) {
      this.arrayNumber = arrayNumberingModel(this.stage.scale.x);
      this.stage.addChild(this.arrayNumber);
    }
    return;
  }

  removeArrayNumber = () => {
    if (this.arrayNumber) this.stage.removeChild(this.arrayNumber);
  }

  removeRoofDimensions() {
    if (this.roofDimensions) {
      this.stage.removeChild(this.roofDimensions);
    }
  }

  drawLeadEdge() {
    const {
      roofsSelector: { mapType },
    } = this.props;

    if (mapType === 'white') {
      return;
    }

    const leadEdge = drawLeadEdge();
    this.background.addChild(leadEdge);
  }

  redrawLeadEdge = () => {
    if (!this.stage || !this.background) {
      return;
    }
    this.background.children = removeLeadEdge(this.background.children);
    const leadEdge = drawLeadEdge();
    this.stage.addChild(leadEdge);
  }


  drawRotatedLeadEdge() {
    const {
      roofsSelector: { mapType },
    } = this.props;

    if (mapType === 'white') {
      return;
    }

    const rotatedLeadEdge = drawRotatedLeadEdge();
    this.background.addChild(rotatedLeadEdge);
  }

  redrawRotatedLeadEdge = () => {
    if (!this.stage || !this.background) {
      return;
    }
    this.background.children = removeRotatedLeadEdge(this.background.children);
    const rotatedLeadEdge = drawRotatedLeadEdge();
    this.stage.addChild(rotatedLeadEdge);
  }


  resizeBackgroundContainer = () => {
    const {
      background: { bgXY },
      tiltedRoof: { roofPitch },
      screenshots,
      projectConfiguration: { productId }
    } = this.props;
    if (!this.backgroundContainer || !this.backgroundContainer.children[0]) {
      return;
    }
    const scale = getScaleFromPitchRoof(roofPitch, productId);
    this.backgroundContainer.children[0].y = (bgXY.y / scale);
    this.backgroundContainer.scale.y = scale;

    drawFogOfWarOnStage(this.outerDiv.offsetWidth, this.canvasHeight, screenshots, this.backgroundContainer.children[0].y);
  }

  _shouldZoomResidentalProducts() {
    const {
      background: { selectedRoofId },
      drawingManager: { roofs },
      roofsSelector: { mapType },
    } = this.props;

    const roof = roofs?.[selectedRoofId];
    return mapType === 'white' && !!roof?.panels;
  }

  /**
   * The core animation loop.
   * Runs at 60fps (via requestAnimationFrame) and handles real-time rendering and updates.
   */
  animate = () => {
    const { zoomedIn, bgAdded } = this.state;
    const {
      productId,
      roofsSelector: { mapType },
      dispatch,
      screenshots,
      background: { bgLoaded, moveMapMode, },
    } = this.props;
    const classicDesignerZoom = mapType === 'white';

    if (this.stopAnimation) {
      return;
    }

    // Optimization: Only update editor dimensions if they have actually changed.
    // This avoids unnecessary state updates and potentially expensive layout recalculations.
    const editorDims = getEditorDims();
    if (!editorDims || editorDims.width !== this.outerDiv.offsetWidth || editorDims.height !== this.canvasHeight) {
      setEditorDims(this.outerDiv.offsetWidth, this.canvasHeight);
    }

    if (
      !zoomedIn &&
      (screenshots || this._shouldZoomResidentalProducts()) &&
      zoomRoof(
        this.stage,
        this.renderer,
        this.outerDiv.offsetWidth,
        this.canvasHeight,
        classicDesignerZoom,
        products[productId],
      )
    ) {
      if (!this.isUnmounted) this.setState(prevState => ({ zoomedIn: true }));
    }

    if (
      (!bgAdded &&
        !bgLoaded &&
        (this.background.getLocalBounds().width === 1280 || this.background.getLocalBounds().width > 0)) ||
      (mapType === 'white' && !bgLoaded)
    ) {
      if (!this.isUnmounted) this.setState({ bgAdded: true });
      dispatch(BG_LOADED());
    }

    if (this.cursor && moveMapMode) {
      this.cursor.alpha = 0.0001;
    } else if (this.cursor) {
      this.cursor.alpha = 1;
    }

    if (this.stage && this.stage.interactiveChildren === moveMapMode) {
      this.stage.interactiveChildren = !moveMapMode;
    }

    // Updates the cursor position and state on every frame if not in screenshot mode.
    !screenshots && drawCursorOnStage(
      this.renderer,
      this.stage,
      this.cursor,
      this.canvas,
      productId,
    );

    // Optimization: updateLayersOrder now only sorts if zIndex order is inconsistent.
    this.stage.children = updateLayersOrder(this.stage.children);

    // Final render of the current frame.
    this.renderer.render(this.stage);
    this.animateFrame = requestAnimationFrame(this.animate);
  }

  renderPreviewWindow() {
    const {
      roofsSelector: { mapType },
    } = this.props;

    if (this.props.isUbuilderLite && mapType === 'white') {
      return null;
    }

    if (mapType === 'google') {
      return (
        <GoogleMapsRoofsSelector
          className="roofs-selector google-maps-roof-selector"
          previewMode={true}
        />
      );
    }

    return (
      <BingMapsRoofsSelector
        className="roofs-selector bing-maps-roof-selector"
        previewMode={true}
      />
    );
  }

  renderEditorForScreenshots = () => {
    if (!this.isEditorReady()) {
      return (
        <div
          style={{
            width: this.screenshotsRes.width,
            height: this.screenshotsRes.height,
          }}
          ref={outerDiv => {
            this.outerDiv = outerDiv;
          }}
        >
          <div
            style={{ opacity: 0 }}
            ref={c => {
              setCanvas(c);
              this.canvas = c;
            }}
          />
          <Loader overrideMsg={this.getLoadingMessage()} />
        </div>
      );
    }

    return (
      <div
        style={{
          width: this.screenshotsRes.width,
          height: this.screenshotsRes.height,
        }}
        ref={outerDiv => {
          this.outerDiv = outerDiv;
        }}
        id="project_info"
      >
        <div
          id="panels_editor"
          ref={c => {
            setCanvas(c);
            this.canvas = c;
          }}
        />
      </div>
    );
  }

  renderEditor = () => {
    const { roofsSelector: { mapType } } = this.props;
    const whiteBgStyle = this.props.isUbuilderLite && mapType === 'white' ? {
      backgroundColor: '#E8EFF6',
      backgroundImage: 'radial-gradient(rgba(0,59,92,0.13) 1px, transparent 1px)',
      backgroundSize: '24px 24px',
    } : {};
    return (
      <div
        ref={(outerDiv) => { this.outerDiv = outerDiv; }}
        id="project_info"
        style={{
          position: 'relative',
          minHeight: this.canvasHeight,
          height: this.canvasHeight,
          overflow: 'hidden',
          ...whiteBgStyle,
        }}
      >
        <FogOfWarComponent />
        <div
          id="panels_editor"
          style={{ width: '100%', height: '100%' }}
          ref={(c) => { setCanvas(c); this.canvas = c; }}
        />
        {this.isEditorReady() && !this.props.isUbuilderLite && <PanelsEditorControls />}
        {!this.props.isUbuilderLite && this.renderPreviewWindow()}
        {!this.isEditorReady() && this.renderLoadingOverlay()}
      </div>
    );
  }

  renderFullEditor() {
    const { productId, isUbuilderLite } = this.props;
    return (
      <div className="panels-editor-container">
        {this.isEditorReady() && !isUbuilderLite && <PanelsEditorDrawer productId={productId} />}
        {this.renderEditor()}
      </div>
    );
  }

  render() {
    const { screenshots } = this.props;

    if (screenshots) {
      return this.renderEditorForScreenshots();
    }

    return this.renderFullEditor();
  }
}

function mapStateToProps(state: appState) {
  return {
    advanceRoofSelecting: state.advanceRoofSelecting,
    background: state.background,
    drawingManager: state.drawingManager,
    editorCursor: state.editorCursor,
    dimensions: state.dimensions,
    history: state.history,
    moduleSelector: state.moduleSelector,
    measurementTool: state.measurementTool,
    leadEdgeRoofSelector: state.leadEdgeRoofSelector,
    panels: state.panels,
    projectConfiguration: state.projectConfiguration,
    roofsSelector: state.roofsSelector,
    roofZones: state.roofZones,
    rubberBand: state.rubberBand,
    scale: state.scale,
    settings: state.settings,
    tiltedRoof: state.tiltedRoof,
    tooltipCursor: state.tooltipCursor,
    obstructions: state.obstructions,
    isDialogOpen: !!state.confirmClearArraysModal?.dialogStyleProps,
    isStaff: state.user.isStaff,
    stage: state.stage,
    isUbuilderLite: state.app.isUbuilderLite,
  };
}

export default connect(mapStateToProps)(PanelsEditor);
